import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { SERVICES } from "@/data/content";
import Seo from "@/lib/Seo";

// Filter groupings — every product belongs to exactly one bucket.
const PRODUCT_FILTERS = [
  { id: "all", label: "All 14", ids: null },
  { id: "sba-term", label: "SBA & Term", ids: ["sba", "term-loans", "franchise", "startup"] },
  { id: "working-capital", label: "Working Capital", ids: ["bloc", "revenue", "working-capital", "mca", "accounts-receivable"] },
  { id: "real-estate", label: "Real Estate", ids: ["bridge", "fix-and-flip", "residential-investment"] },
  { id: "equipment", label: "Equipment & PO", ids: ["equipment", "purchase-order"] },
];

const PageHeader = ({ count }) => (
  <section className="pt-40 pb-16 border-b border-black/10">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8">
      <div className="col-span-12 md:col-span-8">
        <div className="overline">Products · 14 funding solutions</div>
        <h1 className="mt-6 font-serif text-6xl md:text-8xl leading-[0.92] tracking-tight">
          Every kind of capital, <em className="italic text-[color:var(--gold)]">brokered honestly</em>.
        </h1>
      </div>
      <div className="col-span-12 md:col-span-4 md:pt-6">
        <p className="text-[color:var(--bone)]/75 leading-relaxed">
          We work across the full stack of small-business and investor
          finance — from the slow, careful SBA loan to same-day working
          capital, from purchase-order advances to DSCR portfolio loans.
          Fourteen products. One application.
        </p>
      </div>
    </div>
  </section>
);

const FilterBar = ({ active, setActive, counts }) => (
  <div className="sticky top-20 z-30 bg-[color:var(--bg)]/90 backdrop-blur-xl border-b border-black/10">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex flex-wrap gap-2 items-center">
      <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/45 mr-3">
        Filter
      </span>
      {PRODUCT_FILTERS.map((f) => {
        const isActive = active === f.id;
        return (
          <button
            key={f.id}
            data-testid={`product-filter-${f.id}`}
            onClick={() => setActive(f.id)}
            className={`px-4 py-2 font-mono text-[11px] tracking-[0.22em] uppercase rounded-full border transition-all duration-300 ${
              isActive
                ? "bg-[color:var(--gold)] text-black border-[color:var(--gold)]"
                : "border-[color:var(--bone)]/25 text-[color:var(--bone)]/70 hover:border-[color:var(--gold)] hover:text-[color:var(--gold)]"
            }`}
          >
            {f.label} · {counts[f.id]}
          </button>
        );
      })}
    </div>
  </div>
);

const ProductBlock = ({ s, index }) => {
  const flip = index % 2 === 1;
  return (
    <motion.section
      id={s.id}
      layout
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.9, ease: [0.7, 0, 0.2, 1] }}
      className="py-24 md:py-32 border-b border-black/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8 items-center">
        <div className={`col-span-12 md:col-span-6 ${flip ? "md:order-2" : ""}`}>
          <div className="relative aspect-[4/5] md:aspect-[5/6] overflow-hidden">
            <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/12" />
            <div className="absolute top-6 left-6 font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--gold)]">
              Chapter {s.number}
            </div>
          </div>
        </div>
        <div className="col-span-12 md:col-span-6">
          <div className="overline">{s.subtitle}</div>
          <h2 className="mt-4 font-serif text-5xl md:text-6xl leading-[0.95]">{s.title}</h2>
          <p className="mt-6 text-[color:var(--bone)]/75 leading-relaxed max-w-lg">
            {s.description}
          </p>
          <ul className="mt-8 space-y-3">
            {s.highlights.map((h) => (
              <li key={h} className="flex items-center gap-3 text-[color:var(--bone)]/85">
                <Check size={16} className="text-[color:var(--gold)]" />
                <span className="font-mono text-sm tracking-wide">{h}</span>
              </li>
            ))}
          </ul>
          <div className="mt-10 flex gap-6 items-center">
            <Link to="/apply" className="inline-flex items-center gap-3 bg-[#3d3529] text-white px-6 py-3 font-mono text-xs tracking-[0.24em] uppercase hover:bg-[color:var(--gold)] hover:text-black transition-colors">
              Apply in 4 minutes <ArrowRight size={14} />
            </Link>
            <Link to="/contact" className="gold-link font-mono text-xs tracking-[0.24em] uppercase text-[color:var(--bone)]/80">
              Ask a broker
            </Link>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default function Products() {
  const location = useLocation();
  const [active, setActive] = useState("all");

  const counts = useMemo(() => {
    const c = { all: SERVICES.length };
    PRODUCT_FILTERS.filter((f) => f.ids).forEach((f) => {
      c[f.id] = SERVICES.filter((s) => f.ids.includes(s.id)).length;
    });
    return c;
  }, []);

  const visible = useMemo(() => {
    const filter = PRODUCT_FILTERS.find((f) => f.id === active);
    if (!filter || !filter.ids) return SERVICES;
    return SERVICES.filter((s) => filter.ids.includes(s.id));
  }, [active]);

  // Auto-select filter if arriving via /products#some-id and jump to the anchor.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    const filter = PRODUCT_FILTERS.find((f) => f.ids && f.ids.includes(id));
    if (filter) setActive(filter.id);
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 350);
  }, [location]);

  return (
    <div>
      <Seo
        path="/products"
        title="Business Loan & Funding Products"
        description="SBA Loans, Business Line of Credit, Revenue Financing, Equipment Financing, Working Capital, and Merchant Cash Advance — brokered across 75+ lenders."
      />
      <PageHeader count={visible.length} />
      <FilterBar active={active} setActive={setActive} counts={counts} />
      <AnimatePresence mode="popLayout">
        {visible.map((s, i) => (
          <ProductBlock key={s.id} s={s} index={i} />
        ))}
      </AnimatePresence>
      {visible.length === 0 && (
        <div className="py-32 text-center">
          <div className="overline">No products in this filter</div>
        </div>
      )}
    </div>
  );
}
